import React from 'react';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import TextAreaField from '../../../../Components/Form/components/TextAreaField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import { PredefinedPhrase } from './PredefinedPhrasesList';
import InputField from '../../../../Components/Form/components/InputField';
import {
	Phrase,
	PhraseType,
	useCreatePhraseMutation,
	useUpdatePhraseMutation,
} from '../../../../graphql/graphql';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';

interface Props {
	defaultValue?: PredefinedPhrase;
	type: PhraseType;
	onSuccess: (data: Phrase) => void;
}

const PredefinedPhrasesForm: React.FC<Props> = ({
	defaultValue,
	type,
	onSuccess,
}) => {
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [createPhrase] = useCreatePhraseMutation();
	const [updatePhrase] = useUpdatePhraseMutation();

	const validationSchema = yup.object({
		value: yup.string().required(t('validation.predefinedPhrasesForm.value')),
		name: yup.string().notRequired(),
	});

	const formMethods = useForm({
		defaultValues: {
			name: defaultValue?.name ?? '',
			value: defaultValue?.value ?? '',
		},
		resolver: yupResolver(validationSchema),
	});

	const handleSubmit = async (values: any) => {
		const input = { name: values.name, value: values.value, type: type };
		if (defaultValue) {
			const result = await updatePhrase({
				where: { id: defaultValue.id },
				input,
			});
			if ('data' in result) {
				showNotificationSuccess({
					content: t('component.predefinedPhrasesForm.editSuccess'),
				});
				if (result.data.updatePhrase) {
					onSuccess(result.data.updatePhrase);
				}
			}
		} else {
			const result = await createPhrase({
				input,
			});
			if ('data' in result) {
				showNotificationSuccess({
					content: t('component.predefinedPhrasesForm.addSuccess'),
				});
				if (result.data.createPhrase) {
					onSuccess(result.data.createPhrase);
				}
			}
		}
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<InputField
				name='name'
				label='component.predefinedPhrasesForm.name'
				isWrapped
			/>
			<TextAreaField
				name='value'
				label='component.predefinedPhrasesForm.value'
				isWrapped
				isRequired
			/>
			<SubmitButton
				onSubmit={handleSubmit}
				title={defaultValue ? 'base.shared.edit' : 'base.create'}
			/>
		</FormWrapper>
	);
};

export default PredefinedPhrasesForm;
